import { Avatar, Button, Checkbox, Text } from "@nike/eds";
import { useState } from "react";
import { Menu, MenuItem } from "@nike/eds";
import { User } from "../../graphql/graphql";
import type { OktaAuth } from '@okta/okta-auth-js';

function MenuHeader({ user }: { user: User | undefined }) {
  if (!user) {
    return (
      <div className="eds-flex eds-flex--direction-column eds-flex--justify-content-space-between">
        <Text font="body-2" style={{ color: "red", whiteSpace: "nowrap" }}>Not signed in</Text>
      </div>
    );
  }
  return (
    <div className="eds-flex eds-flex--align-items-center eds-gap--16">
      <Avatar initials={user.name} url={user.avatarUrl || ""} />
      <div className="eds-flex eds-flex--direction-column">
        <Text font="body-2">{user.name}</Text>
        <Text font="body-2">{user.email}</Text>
      </div>
    </div>
  );
}

function MenuBody({
  availableTerritories,
  currentTerritories,
  setCurrentTerritories,
  shiftActive
}: {
  availableTerritories: string[];
  currentTerritories: string[];
  setCurrentTerritories: (geos: string[]) => void;
  shiftActive?: boolean;
}) {
  return (
    <>
      {availableTerritories.map((territory) => (
        <MenuItem key={territory}>
          <Checkbox
            id={`cb-${territory}`}
            name={territory}
            label={territory}
            value={territory}
            checked={currentTerritories.includes(territory)}
            disabled={availableTerritories.length === 1 && currentTerritories.includes(territory)}
            onChange={() => {
              const newTerritories = currentTerritories.includes(territory)
                ? currentTerritories.filter((t) => t !== territory)
                : [...currentTerritories, territory];
              setCurrentTerritories(newTerritories);
            }}
          />
        </MenuItem>
      ))}
      {shiftActive && (
        <Text font="body-3" className="eds-spacing--m-8" style={{ color: "#666" }}>
          (Shift key held during menu activation)
        </Text>
      )}
    </>
  );
}

function UserMenuContent({user, children}: { user: User | undefined; children?: React.ReactNode }) {
  if (!user) {
    return null;
  }
  return (
    <div className="eds-flex eds-flex--direction-column eds-gap--8">
      <Text font="title-6" className="eds-spacing--m-8">
        Current Territories
      </Text>
      {children}
    </div>
  );
}


function SignOutLink({ oktaAuth }: { oktaAuth: OktaAuth }) {
  const handleSignOut = async () => {
    try {
      await oktaAuth.signOut({ postLogoutRedirectUri: process.env.REACT_APP_POST_LOGOUT_REDIRECT || window.location.origin });
    } catch (error) {
      console.error("Sign out failed", error);
    }
  };
  return (
      <Button variant="ghost" onClick={handleSignOut}>
        Sign Out
      </Button>
  );
}

export function UserSettingsMenu({
  children,
  currentTerritories,
  setCurrentTerritories,
  availableTerritories,
  user,
  oktaAuth
}: {
  children?: React.ReactNode;
  currentTerritories: string[];
  setCurrentTerritories: (territories: string[]) => void;
  availableTerritories: string[];
  user: User | undefined;
  oktaAuth: OktaAuth;
}) {
  const [open, setOpen] = useState(false);
  // Track whether Shift was held when the menu was activated (click or keyboard)
  const [lastActivationWithShift, setLastActivationWithShift] = useState(false);

  return (
    <div style={{ display: "inline-block", position: "relative" }}>
      <div
        onClick={(e) => {
          // Mouse activation (or touch converted to click). Detect Shift.
          setLastActivationWithShift(e.shiftKey);
          setOpen((prev) => !prev);
        }}
        onKeyDown={(e) => {
          // Keyboard activation with Enter or Space for accessibility.
          if (e.key === 'Enter' || e.key === ' ') {
            setLastActivationWithShift(e.shiftKey || e.getModifierState('Shift'));
            setOpen((prev) => !prev);
            // Prevent page scroll on Space
            if (e.key === ' ') e.preventDefault();
          }
          // Allow quick close with Escape while focusing trigger
          if (e.key === 'Escape' && open) {
            setOpen(false);
          }
        }}
        style={{ cursor: "pointer", display: "inline-block" }}
        tabIndex={0}
        role="button"
        aria-haspopup="menu"
        aria-expanded={open}
        // Data attribute makes it easy to assert in tests
        data-shift-activation={lastActivationWithShift ? 'true' : 'false'}
      >
        {children}
      </div>
      <Menu
        onClickOutside={() => setOpen(false)}
        isOpen={open}
        headerSlot={<MenuHeader user={user} />}
        bodySlot={
          <UserMenuContent user={user}>
            <MenuBody
              availableTerritories={availableTerritories}
              currentTerritories={currentTerritories}
              setCurrentTerritories={setCurrentTerritories}
              shiftActive={lastActivationWithShift}
            />
          </UserMenuContent>
        }
        footerSlot={<SignOutLink oktaAuth={oktaAuth} />}
        placement={"bottom-end"}
      />
    </div>
  );
}

